import { getInstance } from '@/plugins/auth0'
import { Tenant, TenantToken } from '@/store/modules/app/types'
import { getHelpHero } from '@/utils/helphero-instance'
import { TenantHelpers } from '@/utils/tenant-helpers'

declare var consoleLog: any

// Store-instance helpers shared across store modules, registered as a Vuex plugin so that
// `this` inside actions/mutations (the store instance) can call them directly.
export const storePlugins = (store: any) => {
  // Resolves once the first tenant-resolution attempt completes, so bootstrap-time
  // tenant-scoped fetches can await it instead of bailing on a cold-load race.
  let resolveTenantReady: () => void
  store.tenantReady = new Promise<void>((resolve) => {
    resolveTenantReady = resolve
  })
  store.waitForTenant = () => store.tenantReady
  // Idempotent (resolving a settled Promise is a no-op). Call from any tenant-bootstrap
  // conclusion — success OR failure — so fetches awaiting waitForTenant() never hang.
  store.markTenantReady = () => resolveTenantReady()

  store.getMyTenantID = (appState: any): number => {
    if (appState.myTenantIndex >= 0 && appState.myTenantIndex < appState.myTenants.length) {
      const myTenant = appState.myTenants[appState.myTenantIndex] as Tenant
      if (myTenant !== null && myTenant.ID > 0) {
        return myTenant.ID
      }
    }

    return TenantHelpers.InvalidTenantID
  }

  store.setMyTenantIndex = (appState: any, tenantIndex: number) => {
    let resetLibraryDataRequired = false

    if (tenantIndex >= 0 && tenantIndex < appState.myTenants.length) {
      const prevTenantIndex = appState.myTenantIndex
      appState.myTenantIndex = tenantIndex

      sessionStorage.setItem('myTenantIndex', appState.myTenantIndex)
      if (prevTenantIndex !== tenantIndex) {
        resetLibraryDataRequired = true
      }
      appState.myTenant = appState.myTenants[appState.myTenantIndex]
      appState.myTenantToken = appState.myTenantTokens.find((token: TenantToken) => token.id === appState.myTenant.ID)
    } else {
      appState.myTenantIndex = TenantHelpers.InvalidTenantIndex
      sessionStorage.setItem('myTenantIndex', appState.myTenantIndex)
      appState.myTenant = TenantHelpers.GetDefaultTenant()
      appState.myTenantToken = TenantHelpers.GetDefaultTenantToken()
    }

    store.commit('jig/resetTenantJigCountsAndPagination', null, { root: true })
    store.commit('jig/updateLockPayloadTenantId', appState.myTenant.ID, { root: true })

    store.markTenantReady()

    return resetLibraryDataRequired
  }

  store.updateHelpHeroData = (key: string, value: any) => {
    const hlp = getHelpHero()
    if (!hlp) {
      consoleLog('!HelpHero is not ready')
      return
    }
    const data = {} as any
    data[key] = value
    hlp.update(data)
    consoleLog(`Updated HelpHero with data: ${JSON.stringify(data)}`)
  }

  store.resetLibraryData = () => {
    store.commit('teamlibrary/resetLibraryData', null, { root: true })
  }

  store.authService = () => getInstance()
}
